"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import styles from "./AboutCapabilities.module.css";

const chapters = [
  "management",
  "sales",
  "promoters",
  "access",
  "metrics",
] as const;
type Chapter = (typeof chapters)[number];

/** Decorative product metaphors, not live dashboards or usable credentials. */
function CapabilityDiagram({
  chapter,
  lifecyclePhase = 0,
}: {
  chapter: Chapter;
  lifecyclePhase?: number;
}) {
  const t = useTranslations("about.capabilities");
  const steps = [0, 1, 2] as const;

  return (
    <div className={`${styles.visual} ${styles[chapter]}`} aria-hidden="true">
      <span className={styles.visualIndex}>GRUV / LIVE SYSTEM</span>
      {chapter === "management" || chapter === "promoters" ? (
        <div className={styles.flow}>
          {steps.map((step) => (
            <div className={styles.node} key={step}>
              <span className={styles.nodeIcon}>
                <svg
                  viewBox="0 0 48 48"
                  fill="none"
                  focusable="false"
                  aria-hidden="true"
                >
                  {chapter === "management" ? (
                    step === 0 ? (
                      <path d="M10 14h28v24H10zM10 22h28M17 10v8m14-8v8" />
                    ) : step === 1 ? (
                      <>
                        <circle cx="24" cy="17" r="6" />
                        <path d="M12 38v-5a12 12 0 0 1 24 0v5M7 15a5 5 0 0 1 5-5m24 0a5 5 0 0 1 5 5" />
                      </>
                    ) : (
                      <>
                        <path d="M10 14h28M10 24h28M10 34h28" />
                        <circle cx="19" cy="14" r="3" />
                        <circle cx="30" cy="24" r="3" />
                        <circle cx="22" cy="34" r="3" />
                      </>
                    )
                  ) : step === 0 ? (
                    <path d="m21 16 5-5a8 8 0 0 1 11 11l-6 6m-10 4-5 5A8 8 0 0 1 5 26l6-6m7 10 12-12" />
                  ) : step === 1 ? (
                    <>
                      <path d="M8 12h32v9a4 4 0 0 0 0 8v7H8v-7a4 4 0 0 0 0-8zM29 14v20" />
                      <path d="m15 24 4 4 6-8" />
                    </>
                  ) : (
                    <path d="M10 36V24m14 12V12m14 24V18M6 40h36" />
                  )}
                </svg>
              </span>
              <span>{t(`${chapter}.step${step}`)}</span>
            </div>
          ))}
        </div>
      ) : chapter === "sales" ? (
        <div className={styles.tickets}>
          {steps.map((step) => (
            <div className={styles.ticket} key={step}>
              <span className={styles.ticketBrand}>
                GRUV<span>↗</span>
              </span>
              <span className={styles.ticketLabel}>
                {t(`sales.step${step}`)}
              </span>
              <span className={styles.ticketStub}>
                <i />
                <i />
                <i />
                <i />
                <i />
                <i />
                <i />
                <i />
              </span>
            </div>
          ))}
        </div>
      ) : chapter === "access" ? (
        <div className={styles.scanner}>
          <span className={styles.phoneSpeaker} />
          <span className={styles.scannerLabel}>{t("access.label")}</span>
          <div className={styles.scanTarget}>
            <svg
              viewBox="0 0 120 120"
              fill="none"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M6 30V6h24m60 0h24v24m0 60v24H90m-60 0H6V90" />
              <path
                className={styles.pattern}
                d="M26 26h18v18H26zm50 0h18v18H76zM26 76h18v18H26zM58 26v18m0 14h18v18H58zm36 0v18M58 94h18m18-12v12M26 58h18"
              />
            </svg>
            <span className={styles.scanLine} />
          </div>
          <span className={styles.confirmation}>✓</span>
        </div>
      ) : (
        <div
          className={`${styles.lifecycle} ${styles[`lifecyclePhase${lifecyclePhase}`]}`}
          data-lifecycle-phase={lifecyclePhase}
        >
          <svg
            className={styles.orbit}
            viewBox="0 0 400 240"
            fill="none"
            aria-hidden="true"
            focusable="false"
          >
            <ellipse cx="200" cy="120" rx="170" ry="102" />
            <ellipse cx="200" cy="120" rx="135" ry="80" />
            <path
              className={styles.orbitAccent}
              d="M30 120a170 102 0 1 0 340 0a170 102 0 1 0-340 0"
              pathLength="1"
            />
          </svg>
          <div className={styles.timeline}>
            {steps.map((step) => (
              <div
                className={`${styles.phase} ${step === lifecyclePhase ? styles.phaseActive : ""}`}
                key={step}
              >
                <span className={styles.phaseDot} />
                <span>{t(`metrics.step${step}`)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function AboutCapabilities() {
  const t = useTranslations("about.capabilities");
  const sectionRef = useRef<HTMLElement>(null);
  const [lifecyclePhase, setLifecyclePhase] = useState(0);

  useEffect(() => {
    // Content is visible by default. Only an actual intersection starts motion;
    // failed hydration/observation cannot leave a chapter hidden.
    if (
      !window.IntersectionObserver ||
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    )
      return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (
            !entry.isIntersecting ||
            entry.target.hasAttribute("data-revealed")
          )
            continue;
          entry.target.setAttribute("data-revealed", "true");
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -24px 0px" },
    );
    sectionRef.current
      ?.querySelectorAll("[data-capability]")
      .forEach((chapter) => observer.observe(chapter));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const metricsChapter = sectionRef.current?.querySelector<HTMLElement>(
      '[data-capability="metrics"]',
    );
    if (
      !metricsChapter ||
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    )
      return;

    let frame: number | undefined;
    const updatePhase = () => {
      frame = undefined;
      const bounds = metricsChapter.getBoundingClientRect();
      if (bounds.height <= 0) return;

      const viewportHeight = window.innerHeight;
      const scrollRange = Math.max(bounds.height - viewportHeight * 0.3, 1);
      const progress = Math.min(
        1,
        Math.max(0, (viewportHeight * 0.72 - bounds.top) / scrollRange),
      );
      const nextPhase = progress < 0.34 ? 0 : progress < 0.67 ? 1 : 2;
      setLifecyclePhase((current) =>
        current === nextPhase ? current : nextPhase,
      );
    };
    const scheduleUpdate = () => {
      if (frame !== undefined) return;
      frame = window.requestAnimationFrame(updatePhase);
    };

    updatePhase();
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);
    return () => {
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      if (frame !== undefined) window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <section
      className={styles.section}
      ref={sectionRef}
      aria-labelledby="about-capabilities"
    >
      <header className={styles.header}>
        <p className={styles.eyebrow}>
          <span />
          {t("eyebrow")}
        </p>
        <h2 id="about-capabilities">{t("title")}</h2>
        <p className={styles.intro}>{t("subtitle")}</p>
      </header>
      <div className={styles.chapters}>
        {chapters.map((chapter) => (
          <section
            className={styles.chapter}
            data-capability={chapter}
            key={chapter}
            aria-labelledby={`capability-${chapter}`}
          >
            <div className={styles.slide}>
              <div className={styles.copy}>
                <p className={styles.number}>{t(`${chapter}.eyebrow`)}</p>
                <h3 id={`capability-${chapter}`}>{t(`${chapter}.title`)}</h3>
                <p className={styles.description}>
                  {t(`${chapter}.description`)}
                </p>
              </div>
              <CapabilityDiagram
                chapter={chapter}
                lifecyclePhase={chapter === "metrics" ? lifecyclePhase : 0}
              />
            </div>
          </section>
        ))}
      </div>
    </section>
  );
}
